import {isMobile, isSafari, isTablet} from 'react-device-detect';
import type {VideoConstraints} from './types';

const isMobileOrTablet = () => isMobile || isTablet;

type GetMobileOrDesktopVideoConstraintsProps = {
  deviceId?: string;
  facingMode?: string;
  additional?: VideoConstraints;
  optimizedForLiveness?: boolean;
};

const getLivenessOptimizedConstraints = (
  deviceId?: string,
  additional?: VideoConstraints,
) => {
  const additionalConstraints =
    typeof additional === 'object' && additional !== null ? additional : {};

  const baseConstraints = {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: 'user'},
    width: {min: 320, ideal: 800},
    height: {min: 240, ideal: 600},
    frameRate: {ideal: 24},
    resizeMode: 'crop-and-scale' as const,
    aspectRatio: {ideal: 1.333},
    ...additionalConstraints,
  };

  if (isMobileOrTablet()) {
    return {
      ...baseConstraints,
      width: {min: 320, ideal: 640},
      height: {min: 240, ideal: 480},
      aspectRatio: {ideal: 1},
      facingMode: {ideal: 'environment'},
      ...additionalConstraints,
    };
  }

  return baseConstraints;
};

function getReducedVideoConstraints({
  deviceId,
  facingMode,
  additional,
}: GetMobileOrDesktopVideoConstraintsProps) {
  const additionalConstraints = typeof additional === 'object' ? additional : {};

  return {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: facingMode || 'user'},
    width: {min: 320, ideal: 640},
    height: {min: 240, ideal: 480},
    ...additionalConstraints,
  };
}

function getMobileOrDesktopVideoConstraints({
  deviceId,
  facingMode,
  additional,
  optimizedForLiveness = false,
}: GetMobileOrDesktopVideoConstraintsProps) {
  const additionalConstraints = typeof additional === 'object' ? additional : {};

  if (optimizedForLiveness) {
    return getLivenessOptimizedConstraints(deviceId, additionalConstraints);
  }

  const initialConstraints = {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: facingMode || 'user'},
    width: isSafari ? {min: 320, ideal: 1920} : {min: 320, ideal: 4096, max: 4096},
    height: isSafari ? {min: 240, ideal: 1080} : {min: 240, ideal: 2160, max: 2160},
    frameRate: {ideal: 30},
    resizeMode: 'none',
    aspectRatio: {ideal: 1.777},
    ...additionalConstraints,
  };

  if (isMobileOrTablet()) {
    return {
      ...initialConstraints,
      aspectRatio: {ideal: 1},
      facingMode: {ideal: 'environment'},
      ...additionalConstraints,
    };
  }

  if (isSafari && !isMobile) {
    return {
      deviceId: deviceId ? {ideal: deviceId} : undefined,
      width: {min: 320, ideal: 1920},
      height: {min: 240, ideal: 1080},
      ...additionalConstraints,
    };
  }

  return initialConstraints;
}

function isDOMException(error: string | DOMException | Error): error is DOMException {
  return error instanceof DOMException && Boolean(error?.name);
}

type GetPhotoCanvasProps = {
  player?: HTMLVideoElement | null;
  container?: HTMLDivElement | null;
  canvas?: HTMLCanvasElement | null;
  scanArea?: HTMLDivElement | null;
  cropToScanArea?: boolean;
};

function getPhotoCanvas({
  player,
  container,
  scanArea,
  canvas,
  cropToScanArea,
}: GetPhotoCanvasProps) {
  if (!canvas) return;

  const playerWidth = player?.videoWidth ?? 1280;
  const playerHeight = player?.videoHeight ?? 720;
  const playerAR = playerWidth / playerHeight;
  const canvasWidth = container?.offsetWidth ?? 1280;
  const canvasHeight = container?.offsetHeight ?? 1280;
  const canvasAR = canvasWidth / canvasHeight;

  let sourceX: number;
  let sourceY: number;
  let sourceWidth: number;
  let sourceHeight: number;

  if (playerAR > canvasAR) {
    sourceHeight = playerHeight;
    sourceWidth = playerHeight * canvasAR;
    sourceX = (playerWidth - sourceWidth) / 2;
    sourceY = 0;
  } else {
    sourceWidth = playerWidth;
    sourceHeight = playerWidth / canvasAR;
    sourceX = 0;
    sourceY = (playerHeight - sourceHeight) / 2;
  }

  if (cropToScanArea && scanArea && player) {
    const scanAreaRect = scanArea.getBoundingClientRect();
    const scaleY = playerHeight / player.clientHeight;
    const offsetX = (playerWidth - player.clientWidth * scaleY) / 2;
    const offsetY = (playerHeight - player.clientHeight * scaleY) / 2;
    const cropX = offsetX + ((player.clientWidth - scanAreaRect.width) / 2) * scaleY;
    const cropY = offsetY + ((player.clientHeight - scanAreaRect.height) / 2) * scaleY;
    const cropWidth = scanAreaRect.width * scaleY;
    const cropHeight = scanAreaRect.height * scaleY;

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(
        player,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      );
    }

    return canvas;
  }

  canvas.width = sourceWidth;
  canvas.height = sourceHeight;

  const context = canvas.getContext('2d');
  if (context && player) {
    context.drawImage(
      player,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight,
    );
  }

  return canvas;
}

export {
  getMobileOrDesktopVideoConstraints,
  getPhotoCanvas,
  getReducedVideoConstraints,
  isDOMException,
  isMobileOrTablet,
};
